import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetPageP1ContentMutation } from "../../../api/services/pageContent/pageContentApi";
import {
  useGetManageParticipantsMetricsCountByProgramId,
  useGetParticipantActivitiesList,
  useGetParticipantDetailsById,
  useGetProgramDetailById,
  useGetProgramStatusListId,
  useGetSessionsAndEventsList,
} from "../../../api/services/program/programApis";
import { InformationBox, MuiBox, MuiButtonPrimary } from "../../../components";
import PermissionWrapper from "../../../components/permissionWrapper/PermissionWrapper";
import {
  STATUS_BADGE_TYPE,
  StatusBadgeType,
} from "../../../components/statusBadge/StatusBadge";
import {
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../helpers/constants";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getFullName,
  getValueOrBlank,
} from "../../../helpers/utils";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { useToast } from "../../../hooks/useToast";
import { toggleLoader } from "../../../redux/slices/appState/appStateSlice";
import { ManageParticipantsInfo } from "../components/manageParticipantsInfo/ManageParticipantsInfo";
import { ParticipantDetailsCardTypes } from "../modelsAndSchemas/Models";
import { manageParticipantHeaderSiteCoreKeys } from "../sitecore/SiteCoreKeys";
import { CPViewParticipantMainWrapper } from "./CPViewParticipantMain.style";
import { CPViewParticipant } from "./cpViewParticipant/CPViewParticipant";
import { CPViewParticipantFooter } from "./cpViewParticipantFooter/CPViewParticipantFooter";

const VIEW_PARTICIPANT_TAB_INDEX = {
  PROGRAM: 0,
  PARTICIPANT: 1,
  DOCUMENT: 2,
  ACTIVITY_TIMELINE: 3,
  SESSION_EVENTS: 4,
} as const;

export const pageId = PAGE_IDS.COACH_PORTAL_VIEW_PARTICIPANT;

export const CPViewParticipantMain = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { id: programId = "" } = useParams();
  const { commonLabels, fetchLabel } = useSiteCore();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    refetchWithParams: getParticipantDetailsByProgramId,
    data: participantDetailData,
    isPending: isParticipantDetailDataPending,
    isError: isParticipantDetailDataError,
    isSuccess: isParticipantDetailDataSuccess,
  } = useGetParticipantDetailsById();

  const {
    data: activityTimelineData,
    isPending: isActivityTimelinePending,
    isError: isActivityTimelineError,
    refetchWithParams: getActivityTimelineData,
    isSuccess: isActivityTimelineSuccess,
  } = useGetParticipantActivitiesList();

  const {
    data: participantsMetricsCountDataData,
    isPending: isParticipantsMetricsCountDataPending,
    isError: isParticipantsMetricsCountDataError,
    refetchWithParams: refetchParticipantsMetricsCountData,
    isSuccess: participantsMetricsCountDataSuccess,
  } = useGetManageParticipantsMetricsCountByProgramId();

  const {
    data: sessionsEventList,
    isFetching: isSessionsEventsPending,
    isError: isSessionsEventsError,
    refetchWithParams: getSessionsAndEventsList,
  } = useGetSessionsAndEventsList();

  const {
    data: programDetail,
    isFetching: isProgramFetching,
    isError: isProgramError,
    isSuccess: isProgramSuccess,
    refetchWithParams: refetchProgramDetail,
  } = useGetProgramDetailById();

  const {
    data: programStatusList,
    isFetching: isProgramStatusListFetching,
    isError: isProgramStatusListError,
    refetchWithParams: refetchProgramStatusList,
  } = useGetProgramStatusListId();

  const {
    mutate: getPageContent,
    data: pageContentData,
    isPending: isPageContentPending,
    isError,
  } = useGetPageP1ContentMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isParticipantDetailDataPending ||
          isActivityTimelinePending ||
          isParticipantsMetricsCountDataPending ||
          isSessionsEventsPending ||
          isProgramFetching ||
          isProgramStatusListFetching ||
          isPageContentPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isParticipantDetailDataPending,
    isActivityTimelinePending,
    isParticipantsMetricsCountDataPending,
    isSessionsEventsPending,
    isProgramFetching,
    isProgramStatusListFetching,
    isPageContentPending,
  ]);

  useEffect(() => {
    if (
      isParticipantDetailDataError ||
      isActivityTimelineError ||
      isParticipantsMetricsCountDataError ||
      isSessionsEventsError ||
      isProgramError ||
      isProgramStatusListError ||
      isError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isParticipantDetailDataError,
    isActivityTimelineError,
    isParticipantsMetricsCountDataError,
    isSessionsEventsError,
    isProgramError,
    isProgramStatusListError,
    isError,
  ]);

  useEffect(() => {
    if (programId) {
      getParticipantDetailsByProgramId(programId);
      getActivityTimelineData({ id: programId });
      refetchParticipantsMetricsCountData(programId);
      getSessionsAndEventsList(programId);
      refetchProgramDetail(programId);
      getProgramStatusListApi();
    }
  }, [programId]);

  const getProgramStatusListApi = () => {
    refetchProgramStatusList({ programId });
  };

  const handleCardClick = (type: ParticipantDetailsCardTypes) => {
    if (type === "activities" || type === "assessments") {
      setSelectedTabIndex(VIEW_PARTICIPANT_TAB_INDEX.ACTIVITY_TIMELINE);
    } else if (type === "events") {
      setSelectedTabIndex(VIEW_PARTICIPANT_TAB_INDEX.SESSION_EVENTS);
    }
  };

  const getParticipantName = () => {
    return getFullName(
      participantDetailData?.firstName,
      participantDetailData?.lastName,
    );
  };

  const participantDetails = useMemo(() => {
    if (
      isActivityTimelineSuccess &&
      isProgramSuccess &&
      isParticipantDetailDataSuccess &&
      participantsMetricsCountDataSuccess &&
      programDetail
    )
      return {
        activitiesCount: activityTimelineData?.length ?? 0,
        clientName: getValueOrBlank(programDetail?.clientName),
        clientCode: getValueOrBlank(programDetail?.clientCode),
        participantName: getParticipantName(),
        assessmentCount: participantsMetricsCountDataData.assessmentCount,
        sessionAndEventsCount:
          participantsMetricsCountDataData.completedSessionsAndEventsCount,
        programStatusId: programDetail.programStatus as StatusBadgeType,
        nextContactDate: getValueOrBlank(
          convertToUserFormat(
            participantDetailData?.nextContactDate,
            userCountryDateFormat,
          ),
        ),
      };
  }, [
    isActivityTimelineSuccess,
    isProgramSuccess,
    isParticipantDetailDataSuccess,
    participantsMetricsCountDataSuccess,
    programDetail,
    activityTimelineData,
    participantDetailData,
    participantsMetricsCountDataData,
    userCountryDateFormat,
  ]);

  const pageContent = () => {
    getPageContent({
      PageName: "OnboardingPages",
      MarketName: getValueOrBlank(participantDetailData?.marketName),
      language: participantDetailData?.preferredLanguage,
      Persona: programDetail?.nextPersona ?? "",
    });
  };

  useEffect(() => {
    if (
      isParticipantDetailDataSuccess &&
      isProgramSuccess &&
      participantDetailData?.isRegisteredInNext
    ) {
      pageContent();
    }
  }, [
    isProgramSuccess,
    isParticipantDetailDataSuccess,
    participantDetailData?.isRegisteredInNext,
  ]);

  const handleCancel = () => {
    navigate(PAGE_URLS.COACH_PORTAL_SEARCH_PARTICIPANTS, {
      replace: true,
    });
  };

  const handleEdit = () => {
    type StateType = { tabValue: number; from?: string };
    const state: StateType = {
      tabValue: selectedTabIndex,
      from: PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT,
    };

    navigate(`${PAGE_URLS.COACH_PORTAL_EDIT_PARTICIPANT}/${programId}`, {
      state,
    });
  };

  const isProgramClosedStatus =
    STATUS_BADGE_TYPE.closed === programDetail?.programStatus;

  return (
    <CPViewParticipantMainWrapper data-testid="manage-participants-main-wrapper">
      <MuiBox className="header-left">
        <InformationBox
          title={fetchLabel(
            manageParticipantHeaderSiteCoreKeys.manageParticipantHeaderTitle,
            manageParticipantHeaderSiteCoreKeys.viewParticipantHeader,
          )}
        />

        <PermissionWrapper permission={MODULE_PERMISSIONS.COACH_PORTAL_EDIT}>
          {!isProgramClosedStatus && (
            <MuiButtonPrimary onClick={handleEdit} id={`${pageId}-edit`}>
              {commonLabels.edit}
            </MuiButtonPrimary>
          )}
        </PermissionWrapper>
      </MuiBox>
      <ManageParticipantsInfo
        participantDetails={participantDetails}
        onCardClick={handleCardClick}
        data-testid="manage-participants-info"
      />

      <CPViewParticipant
        programDetail={programDetail}
        participantDetail={participantDetailData}
        sessionsEventList={sessionsEventList}
        programStatusList={programStatusList ?? []}
        pageContentData={pageContentData}
        setSelectedTabIndex={setSelectedTabIndex}
        selectedTabIndex={selectedTabIndex}
        getProgramStatusListApi={getProgramStatusListApi}
      />

      <CPViewParticipantFooter
        pageId={pageId}
        handleNavigateBack={handleCancel}
        handleEdit={handleEdit}
        showEditButton={!isProgramClosedStatus}
      />
    </CPViewParticipantMainWrapper>
  );
};
